import { parseJson, abbreviateNumber, safeGet } from "src/shared/utils";
import MetricOperations, { PageTypes, timerange } from "./MetricOperations";
import { SelectedFilterValues } from "src/shared/components/FiltersPanev2";
import { IsTabHidden } from "src/shared/utils/customTab";
import { getServiceName, ServiceName } from "src/utils/getservicename";
const UNDEFINED = "undefined";

const defaultOrderedDimensionsRx = [/time/i, /city/i, /(area|zone)/i];

export type Aggregation = {
  id: string;
  label: string;
};

export enum DetailMetricType {
  METRIC = "metric",
  PIN = "pin",
}

export default class DetailsModel extends MetricOperations {
  public id: number;
  public currentChartId: number;
  public currentGoal: string;
  public benchMark: string;
  public pastChartId: number;
  public bigNumber: string | undefined;
  public vCompare: string | undefined;
  public progress: boolean | undefined;
  public vPlan: string | undefined;
  public planProgress: boolean | undefined;
  public lastUpdatedTimeStamp?: string;
  public lastUpdatedTimeStampRaw?: number;
  public isRenderLastHour: boolean;
  public lastHourPlaceholder: string;
  public lastHourBigNumber: string | undefined;
  public lastHourCutoffLabel: string | undefined;
  public lastHourVCompare: string | undefined;
  public lastHourWowProgress: boolean | undefined;
  public lastHourVPlan: string | undefined;
  public lastHourPlanProgress: boolean | undefined;
  public trendChartId: number;
  public planOrderLineChartId: number;
  public successful?: boolean;
  public trendChartData: any;
  public pastTrendChartId: any;
  public tableChartId: number;
  public filterChartId: number;
  public label: string;
  public aggregations: Array<Aggregation>;
  public defaultSelectedDimensions: Array<Aggregation>;
  public isPercentage: boolean;
  public isAnomalous: 0 | 1 | 2;
  public isPositive: boolean;
  public isStaleMetric: string; // TODO: Rename to `isStale`
  public filtersFromPin?: SelectedFilterValues;
  public metricId?: number; // used when id refers to pin id
  public lowerBound: number;
  public upperBound: number;
  public owner: string;
  public dataset: string;
  public freshness: number;
  public sla: number;
  public time_stamp: string;
  public isHidden: string;
  public message: string;
  public unit: string | undefined;

  public constructor(obj) {
    super();
    this.id = obj.id;
    this.label = obj.label;
    this.currentChartId = obj.currentChartId;
    const performanceCharts = parseJson(obj.performanceChartIds);
    this.currentGoal = safeGet(performanceCharts, "[0]", 0)
      ? abbreviateNumber(safeGet(performanceCharts, "[0]", 0))
      : "-";
    this.benchMark = safeGet(performanceCharts, "[1]", 0)
      ? abbreviateNumber(safeGet(performanceCharts, "[1]", 0))
      : "0"; // Old logic: show - if value = 0, new logic: show the number directly
    this.pastChartId = obj.pastChartId;
    this.trendChartId = obj.trendChartId;
    this.planOrderLineChartId = obj.planOrderLineChartId;
    this.pastTrendChartId = obj.pastTrendChartId || obj.trendChartId;
    this.filterChartId = obj.filterChartId;
    this.tableChartId = obj.tableChartId;
    this.aggregations = parseJson(obj.aggregations);
    this.defaultSelectedDimensions = this.getDefaultSelectedDimensions(this.aggregations);
    this.isPercentage = safeGet(obj, "isPercentage");
    this.isPositive = safeGet(obj, "isPositive", true);
    this.filtersFromPin = obj.filters ? this.parseFilters(parseJson(obj.filters) || []) : undefined;
    this.metricId = obj.metricId || obj.id;
    this.isAnomalous = safeGet(obj, "isAnomalous");
    this.lowerBound = safeGet(obj, "lowerBound", -10);
    this.upperBound = safeGet(obj, "upperBound", 10);
    this.isStaleMetric = "";
    this.owner = obj.owner;
    this.dataset = obj.dataset;
    this.freshness = obj.freshness;
    this.sla = obj.sla;
    this.time_stamp = obj.time_stamp;
    this.isHidden = obj.isHidden;
    this.message = obj.message;
    this.unit = obj.unit;
    this.isRenderLastHour = safeGet(obj, "isRenderLastHour", false);
    this.lastHourPlaceholder = "-";
  }

  public static createDetail(obj) {
    return new DetailsModel(obj);
  }

  public static constructNestedTreePayload(
    pinId: number,
    metricId: number,
    tableChartId: number,
    firstSelectedValue: string,
    secondSelectedValue: string,
    lastSelectedValue: string,
    chronicFilter: string,
    adhocFilters: IObject<string[]>,
    filterConfig?: any
  ) {
    let dimensionsCount = lastSelectedValue ? 3 : secondSelectedValue ? 2 : firstSelectedValue ? 1 : 0;

    const result: any[] = [];
    while (dimensionsCount > 0) {
      result.push(
        DetailsModel.getFiltersPayload(
          pinId,
          metricId,
          tableChartId,
          firstSelectedValue,
          dimensionsCount > 1 ? secondSelectedValue : "",
          dimensionsCount === 3 ? lastSelectedValue : "",
          chronicFilter,
          adhocFilters,
          filterConfig
        )
      );

      dimensionsCount--;
    }
    return result.reverse();
  }

  public static getFiltersPayload(
    pinId,
    metricId: number,
    tableChartId: number,
    firstSelectedValue: string,
    secondSelectedValue: string,
    lastSelectedValue: string,
    chronicFilter: string,
    adhocFilters: IObject<string[]>,
    filterConfig
  ) {
    const groupBy: Array<any> = [];
    if (firstSelectedValue || secondSelectedValue || lastSelectedValue) {
      firstSelectedValue && groupBy.push(firstSelectedValue);
      secondSelectedValue && groupBy.push(secondSelectedValue);
      lastSelectedValue && groupBy.push(lastSelectedValue);
    }

    const adhocFiltersParsed = Object.entries(adhocFilters).map(([key, value]) => ({
      field: key,
      value,
    }));

    const chronicFiltersParsed = {
      ...this.parseChronicFilter(filterConfig, chronicFilter, "currentChart"),
      // ...this.chronicFilterValues[chronicFilter],
      group_by: groupBy,
    };
    // chronicFiltersParsed["group_by"] = groupBy;

    return {
      slice_id: tableChartId,
      metric_id: metricId,
      time_view: "filters",
      filters: {
        adhoc_filters: adhocFiltersParsed,
        chronic_filters: chronicFiltersParsed,
      },
      ...(pinId ? { pin_id: pinId } : null),
    };
  }

  public static getDetailsBulkChartRequest(
    obj: DetailsModel,
    adhocFilters: SelectedFilterValues,
    chronicFilter,
    filterConfig,
    metricType: DetailMetricType
  ) {
    const filters = Object.entries(adhocFilters)
      .map(([key, value]) => ({
        field: key,
        value,
      }))
      .filter((filter) => !!filter.value.length);

    const request = MetricOperations.getBulkChartRequest(
      chronicFilter,
      [obj],
      PageTypes.L3,
      metricType,
      filters,
      filterConfig
    );
    return request;
  }

  private getDefaultSelectedDimensions = (aggregations: Aggregation[]) => {
    return defaultOrderedDimensionsRx
      .map((rx) => aggregations.find((a) => rx.test(a.id)))
      .filter(Boolean)
      .slice(0, 1) as Aggregation[];
  };

  public calculateDataFromCharts(
    chartsResponse: Array<any>,
    timeTab: string
    // label: string TODO: if label also required in chart legend
  ) {
    if (!this.isStaleMetric) {
      chartsResponse.forEach((chartData) => {
        // https://swiggy.atlassian.net/browse/ANOBIS-224
        if (chartData.time_view === "currentChartId" || chartData.time_view === "trendChartId") {
          if (!this.isStaleMetric) {
            this.isStaleMetric = chartData.isStaleMetric;
          }
        }
      });
    }

    const currentChart = (chartsResponse || []).find(
      (item) =>
        // item["pin_id"] === this.id &&
        item["metric_id"] === this.metricId &&
        item["slice_id"] === this.currentChartId &&
        item["time_view"] === "currentChartId"
    );

    const pastChart = (chartsResponse || []).find(
      (item) =>
        // item["pin_id"] === this.id &&
        item["metric_id"] === this.metricId &&
        item["slice_id"] === this.pastChartId &&
        item["time_view"] === "pastChartId"
    );

    if (typeof currentChart !== UNDEFINED && typeof pastChart !== UNDEFINED) {
      const currentChartData = parseJson(safeGet(currentChart, "data", {}));
      const currentChartDataPoints = safeGet(currentChartData, "data.data", []);
      const bigNumber = safeGet(currentChartDataPoints, `[0][0]`, 0);
      // currentChartDataPoints[currentChartDataPoints.length - 1][1];
      this.bigNumber = abbreviateNumber(bigNumber);

      this.bigNumber = !IsTabHidden(this.isHidden, timeTab)
        ? this.unit
          ? this.unit === "INR"
            ? "₹" + this.bigNumber
            : this.bigNumber.concat(this.unit)
          : this.bigNumber
        : "-";

      this.benchMark = !IsTabHidden(this.isHidden, timeTab)
        ? this.unit
          ? this.unit === "INR"
            ? "₹" + this.benchMark
            : this.benchMark.concat(this.unit)
          : this.benchMark
        : "";

      const pastChartData = parseJson(safeGet(pastChart, "data", {}));
      const pastChartDataPoints = !IsTabHidden(this.isHidden, timeTab) ? safeGet(pastChartData, "data.data", []) : [];
      const pastBigNumber = safeGet(pastChartDataPoints, `[0][0]`);
      const vCompare = this.isPercentage
        ? bigNumber - pastBigNumber
        : pastBigNumber === 0
        ? 100
        : ((bigNumber - pastBigNumber) * 100) / pastBigNumber;
      const cPrefix = this.isPercentage ? MetricOperations.comparePrefixPercent : MetricOperations.comparePrefix;
      this.progress = vCompare > 0;
      const isPastNumberPresent = safeGet(pastChartDataPoints, `[0][0]`, false) !== false;
      this.vCompare = isPastNumberPresent ? abbreviateNumber(vCompare).concat(cPrefix[timeTab]) : "";
      this.isAnomalous = vCompare < this.lowerBound || vCompare > this.upperBound ? 2 : this.isAnomalous ? 1 : 0;

      this.successful = safeGet(currentChartDataPoints, `[0][0]`, false) !== false;
      const trendChartData = chartsResponse.find((item) =>
        // item["pin_id"] === this.id && item["metric_id"] === this.metricId && item["slice_id"] === this.trendChartId
        item["metric_id"] === this.metricId && item["slice_id"] === !IsTabHidden(this.isHidden, timeTab)
          ? this.trendChartId
          : -1 && item["time_view"] === "trendChartId"
      );
      const pastTrendChartData = chartsResponse.find((item) =>
        item["metric_id"] === this.metricId && item["slice_id"] === !IsTabHidden(this.isHidden, timeTab)
          ? this.pastTrendChartId
          : -1 && item["time_view"] === "pastTrendChartId"
      );
      // Extract plan order line chart data if available
      const planOrderLineChartData = chartsResponse.find(
        (item) => item["metric_id"] === this.metricId && item["time_view"] === "planOrderLineChartId"
      );

      if (planOrderLineChartData && !IsTabHidden(this.isHidden, timeTab)) {
        const planParsed = parseJson(safeGet(planOrderLineChartData, "data", {}));
        const planChartValues: Array<{ x: number; y: number }> = safeGet(planParsed, "data[0].values", []);
        if (planChartValues.length > 0) {
          const plannedBigNumber = planChartValues.reduce((sum, point) => sum + (point.y || 0), 0);
          if (plannedBigNumber !== 0) {
            const planDiff = this.isPercentage
              ? bigNumber - plannedBigNumber
              : ((bigNumber - plannedBigNumber) * 100) / plannedBigNumber;
            this.planProgress = planDiff > 0;
            this.vPlan = abbreviateNumber(planDiff).concat("% vs PLAN");
          }
        }
      }

      this.trendChartData = MetricOperations.parseTrendChartData(
        this.isHidden,
        timeTab,
        trendChartData,
        pastTrendChartData,
        timeTab as timerange,
        planOrderLineChartData
      );

      // "Until last hour" card — data comes from a separate render_filter_slices call
      // whose time filter was derived from lastUpdatedTimeStampRaw (backend source of truth).
      // We use the same raw timestamp here for the label so payload and label always agree.
      if (this.isRenderLastHour && timeTab === "Today") {
        // Backend stores IST time in UTC fields (getTimeFromTimestamp relies on this).
        // Use getUTC* to read IST hours/minutes directly.
        const ts = this.lastUpdatedTimeStampRaw ? new Date(this.lastUpdatedTimeStampRaw) : null;
        let istHour: number;
        let istMinute: number;
        if (ts && !isNaN(ts.getTime())) {
          istHour = ts.getUTCHours();
          istMinute = ts.getUTCMinutes();
        } else {
          const now = new Date();
          istHour = now.getHours();
          istMinute = now.getMinutes();
        }

        const cutoffHour = istMinute >= 30 ? istHour : istHour - 1;

        // Before 00:30 IST there is no valid "last hour" cutoff within today
        if (cutoffHour >= 0) {
          const hour12 = ((cutoffHour + 11) % 12) + 1;
          const ampm = cutoffHour >= 12 ? "PM" : "AM";
          this.lastHourCutoffLabel = `until ${hour12} ${ampm}>`;

          // Use truncated big-number charts fetched via additional render_filter_slices call
          const lastHourCurrentChart = (chartsResponse || []).find(
            (item) =>
              item["metric_id"] === this.metricId &&
              item["slice_id"] === this.currentChartId &&
              item["time_view"] === "lastHourCurrentChartId"
          );
          const lastHourPastChart = (chartsResponse || []).find(
            (item) =>
              item["metric_id"] === this.metricId &&
              item["slice_id"] === this.pastChartId &&
              item["time_view"] === "lastHourPastChartId"
          );
          const lastHourPlanChart = (chartsResponse || []).find(
            (item) =>
              item["metric_id"] === this.metricId &&
              item["slice_id"] === this.planOrderLineChartId &&
              item["time_view"] === "lastHourPlanOrderLineChartId"
          );

          const lastHourCurrentParsed = lastHourCurrentChart ? parseJson(safeGet(lastHourCurrentChart, "data", {})) : null;
          const lastHourCurrentPoints = lastHourCurrentParsed ? safeGet(lastHourCurrentParsed, "data.data", []) : [];
          const actualFromApi = safeGet(lastHourCurrentPoints, `[0][0]`, null);

          const lastHourPastParsed = lastHourPastChart ? parseJson(safeGet(lastHourPastChart, "data", {})) : null;
          const lastHourPastPoints = lastHourPastParsed ? safeGet(lastHourPastParsed, "data.data", []) : [];
          const pastFromApi = safeGet(lastHourPastPoints, `[0][0]`, null);

          if (actualFromApi === null || IsTabHidden(this.isHidden, timeTab)) {
            this.lastHourBigNumber = "-";
            this.lastHourVCompare = "";
            this.lastHourVPlan = undefined;
            this.lastHourWowProgress = undefined;
            this.lastHourPlanProgress = undefined;
          } else {
            const actual = Number(actualFromApi);
            const past = pastFromApi !== null ? Number(pastFromApi) : 0;

            const formattedActual = abbreviateNumber(actual);
            this.lastHourBigNumber = this.unit
              ? this.unit === "INR"
                ? "₹" + formattedActual
                : formattedActual.concat(this.unit)
              : formattedActual;

            const cPrefix = this.isPercentage ? MetricOperations.comparePrefixPercent : MetricOperations.comparePrefix;

            if (past !== 0) {
              const wowDiff = this.isPercentage ? actual - past : ((actual - past) * 100) / past;
              this.lastHourWowProgress = wowDiff > 0;
              this.lastHourVCompare = abbreviateNumber(wowDiff).concat(cPrefix[timeTab]);
            } else {
              this.lastHourVCompare = "";
            }

            if (lastHourPlanChart) {
              const planParsed = parseJson(safeGet(lastHourPlanChart, "data", {}));
              const planChartValues: Array<{ x: number; y: number }> = safeGet(planParsed, "data[0].values", []);
              if (planChartValues.length) {
                // Only sum plan hours that the actual data fully covers.
                // Plan data is hourly starting from midnight; index i = hour i.
                // cutoffHour = last completed hour, so take hours 0..(cutoffHour-1).
                const planPointsInRange = planChartValues.slice(0, cutoffHour);
                const plannedBigNumber = planPointsInRange.reduce((sum, point) => sum + (point.y || 0), 0);
                if (plannedBigNumber !== 0) {
                  const planDiff = this.isPercentage
                    ? actual - plannedBigNumber
                    : ((actual - plannedBigNumber) * 100) / plannedBigNumber;
                  this.lastHourPlanProgress = planDiff > 0;
                  this.lastHourVPlan = abbreviateNumber(planDiff).concat("% vs PLAN");
                }
              }
            }
          }
        } else {
          this.lastHourBigNumber = "-";
          this.lastHourVCompare = "";
          this.lastHourVPlan = undefined;
          this.lastHourCutoffLabel = undefined;
        }
      }
    } else {
      this.successful = false;
    }
    return this;
  }

  public parseFilters(filter: Array<IObject<any>>) {
    const object = {};
    filter.map((item) => {
      object[item.field] = item.value;
    });
    return object;
  }
}

export type AccordianNode = {
  name: string;
  count: number;
  type: string;
  percentage?: number;
  children?: Array<AccordianNode>;
};

export class NestedTreeModel {
  public static constructModel(data: any, dimensionsMap: IObject<string>) {
    return new NestedTreeModel(data, dimensionsMap);
  }

  public data: AccordianNode[] = [];
  private getDimensionNameFromLevelScoped: (level: number) => string | undefined;

  public constructor(responseData: any, dimensionsMap: IObject<string>) {
    this.getDimensionNameFromLevelScoped = this.getDimensionNameFromLevel(dimensionsMap);
    this.data = this.buildModel(responseData);
  }

  private getDimensionNameFromLevel = (dimensionMap: IObject<string>) => (level: number) => {
    switch (level) {
      case 0:
        return dimensionMap.FIRST_DIMENSION;
      case 1:
        return dimensionMap.SECOND_DIMENSION;
      case 2:
        return dimensionMap.THIRD_DIMENSION;
    }
  };
  /** TODO: Fix the default type specified as COUNT(order_id) */
  private getCountKey = (data: any): string => safeGet(data, "label", "COUNT(order_id)");

  /** CurrentModel has higher depth than accumulatedVal  */
  private mapArrays(accumulatedVal: AccordianNode[], currentModel: AccordianNode[]) {
    if (!accumulatedVal || safeGet(accumulatedVal, "length") === 0) {
      accumulatedVal = currentModel;
    } else {
      accumulatedVal.forEach((acRow) => {
        const foundNode = currentModel.find((cm) => cm.name === acRow.name);
        if (foundNode && foundNode.children) {
          acRow.children = this.mapArrays(acRow.children || [], foundNode.children);
        }
      });
    }
    return accumulatedVal;
  }
  public buildModel(responseData: Array<any>) {
    return responseData.reduce((acc: AccordianNode[], responseDatum) => {
      const builtModel: AccordianNode[] = this.buildModelIndividual(responseDatum);
      if (acc) {
        // acc.forEach(a => )
        acc = this.mapArrays(acc, builtModel);
      }
      return acc ? acc : builtModel;
    }, null);
  }
  private buildModelIndividual(responseData: any) {
    let result: AccordianNode[] = [];
    try {
      const parsedDataResponse = parseJson(safeGet(responseData, "data"));
      const COUNT_KEY = this.getCountKey(parsedDataResponse);

      const processNode = (
        node: IObject<any>,
        accumulator: Array<AccordianNode> = [],
        dimensionLevel: number,
        parentNode?: AccordianNode
      ) => {
        const dimensionName = this.getDimensionNameFromLevelScoped(dimensionLevel);
        if (!node[dimensionName!]) return;
        const foundAccordianNode = accumulator.find((r) => r.name === node[dimensionName!]);
        if (foundAccordianNode && dimensionLevel < 2) {
          processNode(node, foundAccordianNode.children, dimensionLevel + 1, foundAccordianNode);
        } else {
          // if (parentNode) parentNode.count += node[COUNT_KEY];
          accumulator.push({
            name: node[dimensionName!],
            count: node[COUNT_KEY],
            type: dimensionName!,
            children: dimensionLevel < 2 ? processNode(node, [], dimensionLevel + 1) : [],
          });
        }
        return accumulator;
      };

      const { records }: { records: Array<IObject<string | number>> } = parsedDataResponse.data;

      result = records.reduce((acc, node) => {
        processNode(node, acc, 0, undefined);
        return acc;
      }, []);
      result = this.processResult(result);
    } catch (e) {
      console.error(`Error parsing the NestedTree chart data: `, e);
    }
    return result;
  }

  private processResult(result: Array<AccordianNode>) {
    const cumulativeCount = result.reduce((acc, node) => acc + node.count, 0);
    return result.map((node) => ({
      ...node,
      percentage: (node.count / cumulativeCount) * 100,
      children: node.children ? this.processResult(node.children) : [],
    }));
  }
}
